import { useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'next-i18next'
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

import orderAggregatorService from '@src/quince-site-common/src/services/orderAggregatorService'
import {
  OrdersType,
  OrderDetailsType as OrderAggregatorOrderType,
} from '@src/quince-site-common/src/services/orderAggregatorService/orderAggregatorService.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { SearchOptionsPayload } from '../components/types'
import { getFmoTrackingId } from '../utils'

// API fetches 50 orders at once, but we show only 10 at a time on UI
const API_PAGE_SIZE = 50
const UI_PAGE_SIZE = 10

interface OrdersListContextProps {
  orders: OrderAggregatorOrderType[]
  isLoading: boolean
  loaderRef: React.RefObject<HTMLDivElement>
  hasMore: boolean
  error: string | null
}
interface OrdersListProviderProps {
  children: ReactNode
  onError: (err: any) => void
  searchParams: SearchOptionsPayload
  initialData?: OrdersType
}
const InfiniteListContext = createContext<OrdersListContextProps>({
  orders: [],
  isLoading: false,
  loaderRef: { current: null },
  hasMore: true,
  error: null,
})
export const InfiniteListProvider = ({ children, onError, searchParams, initialData }: OrdersListProviderProps) => {
  // Orders currently displayed on UI
  const [displayedOrders, setDisplayedOrders] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { user } = useCurrentUser()
  const { customerMetadata } = user ?? {}

  const ordersBuffer = useRef<any[]>([])
  const displayedCount = useRef(0)
  const currentApiPage = useRef(0)
  const hasMoreFromApi = useRef(true)

  const { t } = useTranslation()
  const loaderRef = useRef(null)

  // Fetch next batch of 50 orders from API
  const fetchFromApi = useCallback(async () => {
    if (!hasMoreFromApi.current) return false

    setIsLoading(true)
    try {
      const {
        orders = [],
        page,
        hasMore: apiHasMore,
      } = await orderAggregatorService.getSearchResults({
        ...searchParams,
        page: currentApiPage.current + 1,
        size: API_PAGE_SIZE,
      })
      trackEvent(TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS, {
        formData: {
          searchField: searchParams.searchType,
          page: page,
          orderResults: orders?.length,
          ...getFmoTrackingId(customerMetadata),
        },
      })

      // Add new orders to buffer
      ordersBuffer.current = [...ordersBuffer.current, ...orders]
      currentApiPage.current = page
      hasMoreFromApi.current = apiHasMore
      setError(null)
      setIsLoading(false)
      return orders.length > 0
    } catch (error) {
      setIsLoading(false)
      hasMoreFromApi.current = false
      setHasMore(false)
      setError(t('failed_to_fetch_orders'))
      onError(error)
      return false
    }
  }, [searchParams, t, onError])

  // Show next 10 orders from buffer
  const showNextBatch = useCallback(() => {
    const nextBatch = ordersBuffer.current.slice(displayedCount.current, displayedCount.current + UI_PAGE_SIZE)

    if (nextBatch.length > 0) {
      setDisplayedOrders(prev => [...prev, ...nextBatch])
      displayedCount.current += nextBatch.length
    }

    // Check if there are more orders to show
    const hasMoreInBuffer = displayedCount.current < ordersBuffer.current.length
    const canFetchMore = hasMoreFromApi.current
    setHasMore(hasMoreInBuffer || canFetchMore)
  }, [])

  // Load more data - either from buffer or fetch from API
  const loadMoreData = useCallback(async () => {
    if (isLoading) return

    // Check if we have more orders in the buffer
    const hasMoreInBuffer = displayedCount.current < ordersBuffer.current.length

    if (hasMoreInBuffer) {
      // Show next 10 from buffer (no API call)
      showNextBatch()
    } else if (hasMoreFromApi.current) {
      // Buffer exhausted, fetch next 50 from API
      const fetched = await fetchFromApi()
      if (fetched) {
        showNextBatch()
      }
    } else {
      // No more data anywhere
      setHasMore(false)
    }
  }, [isLoading, fetchFromApi, showNextBatch])

  // Initialize with initial data and reset when searchParams change
  useEffect(() => {
    if (searchParams && initialData) {
      // Initialize buffer with initial data
      ordersBuffer.current = initialData.orders ?? []
      displayedCount.current = 0
      currentApiPage.current = initialData.page ?? 0
      hasMoreFromApi.current = initialData.hasMore ?? true

      // Show first 10 orders
      const firstBatch = ordersBuffer.current.slice(0, UI_PAGE_SIZE)
      setDisplayedOrders(firstBatch)
      displayedCount.current = firstBatch.length

      const hasMoreInBuffer = displayedCount.current < ordersBuffer.current.length
      setHasMore(hasMoreInBuffer || hasMoreFromApi.current)
    } else if (searchParams && !initialData) {
      // Reset state when search params change but no initial data
      ordersBuffer.current = []
      displayedCount.current = 0
      currentApiPage.current = 0
      hasMoreFromApi.current = true
      setDisplayedOrders([])
      setHasMore(true)
    }
  }, [searchParams, initialData])

  useEffect(() => {
    const currentLoaderRef = loaderRef.current
    if (!currentLoaderRef || !hasMore) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          void loadMoreData()
        }
      },
      { rootMargin: '100px' },
    )

    observer.observe(currentLoaderRef)

    return () => {
      observer.unobserve(currentLoaderRef)
    }
  }, [loadMoreData, hasMore, displayedOrders])

  return (
    <InfiniteListContext.Provider
      value={{
        orders: displayedOrders,
        isLoading,
        loaderRef,
        hasMore,
        error,
      }}
    >
      {children}
    </InfiniteListContext.Provider>
  )
}

export const useInfiniteList = () => {
  return useContext(InfiniteListContext)
}
